export type Category = 'oracle' | 'family' | 'custom' | 'unknown';

export interface Component {
  name: string;
  location?: string;
  state?: string;
}

export interface Classified extends Component {
  category: Category;
  note?: string;
}

export const CATEGORY_LABEL: Record<Category, string> = {
  oracle: 'Oracle-shipped (reference list)',
  family: 'Oracle-family by name; verify',
  custom: 'Custom, by name or location',
  unknown: 'Not recognized; verify',
};

export const CATEGORY_ORDER: Category[] = ['custom', 'unknown', 'family', 'oracle'];

// Component names that ship with WebCenter Content or Inbound Refinery in
// the 11g/12c line. A reference list, not an exhaustive one: a name missing
// here is "not recognized", which is not the same as "custom".
const ORACLE_STOCK = new Set(
  [
    'AppAdapterCore',
    'AppAdapterEBS',
    'AppAdapterPSFT',
    'AppAdapterUniversal',
    'ArchiverReplicationExceptions',
    'AutoSuggestConfig',
    'BpelIntegration',
    'CheckoutAndOpenInNative',
    'CheckSCS',
    'ComponentTool',
    'ConfigMigrationUtility',
    'ContentAccess',
    'ContentBasket',
    'ContentCategorizer',
    'ContentFolios',
    'ContentTracker',
    'ContentTrackerReports',
    'CoreWebDav',
    'DBSearchContainsOpSupport',
    'DesktopIntegrationSuite',
    'DesktopTag',
    'DynamicConverter',
    'EmailMetadata',
    'ExtranetLook',
    'FileStoreProvider',
    'Folders_g',
    'FormEditor',
    'FrameworkFolders',
    'InboundRefinerySupport',
    'iTextComponent',
    'LinkManager8',
    'Localization',
    'NativeOsUtils',
    'OracleQueryOptimizer',
    'OracleTextSearch',
    'PDFExportSupport',
    'PDFWatermark',
    'ProxyConnections8',
    'RecordsManagement',
    'SESCrawlerExport',
    'SiteStudio',
    'SiteStudioExternalApplications',
    'SiteStudioPublisher',
    'ThreadedDiscussions',
    'UserProfileSchemaLoader',
    'WebCenterConfigure',
    'WebUrlMapPlugin',
    'WsdlGenerator',
    'YahooUserInterfaceLibrary',
    'ZipRenditionManagement',
  ].map((n) => n.toLowerCase()),
);

const ORACLE_FAMILY = /^(oracle|webcenter|wcc|ucm|idc|appadapter|sitestudio|rma|dis|framework|folders|imaging|bpel|ses|ibr|conversion|pdf|tiff|xml|msoffice|winnative)/i;
const CUSTOM_NAME = /custom|_cust\b|^cust/i;
const CUSTOM_PATH = /[\\/]custom[\\/]/i;

const NOTES: Record<string, string> = {
  folders_g:
    'Folders_g is the older folders model. 12c and 14c environments are documented around FrameworkFolders; confirm the folders migration path in the WebCenter Content upgrade guide before planning the upgrade.',
  sitestudio: 'Confirm the status of Site Studio for the 14c release in Oracle’s documentation before planning around it.',
  contenttracker: 'Identify whether Content Tracker history is required and validate its treatment on the chosen path.',
  recordsmanagement: 'Records Management carries retention rules and dispositions. Plan its validation as its own test track.',
};

export function parseInventory(text: string): { rows: Component[]; error?: string; duplicates: number } {
  if (text.length > 200000) return { rows: [], duplicates: 0, error: 'Use a listing smaller than 200,000 characters.' };
  const out: Component[] = [];
  const fail = (error: string) => ({ rows: [], duplicates: 0, error });
  if (/@ResultSet/i.test(text)) {
    const blocks = [...text.matchAll(/@ResultSet\s+(\S+)\r?\n(\d+)\r?\n([\s\S]*?)@end/g)];
    const componentBlocks = blocks.filter(m => /^Components$/i.test(m[1]));
    if (!componentBlocks.length) return fail('No complete Components ResultSet was found. Paste a Components block or one component name per line.');
    if ((text.match(/@ResultSet\s+Components\b/gi) || []).length !== componentBlocks.length) return fail('A Components ResultSet is incomplete. Check its @end marker.');
    for (const m of componentBlocks) {
      const n = Number(m[2]);
      if (!Number.isInteger(n) || n < 1 || n > 100) return fail('The Components column count is invalid.');
      const lines = m[3].replace(/\r/g, '').split('\n');
      if (lines.at(-1) === '') lines.pop();
      const cols = lines.slice(0, n).map(c => c.trim());
      const cells = lines.slice(n);
      const nameIndex = cols.findIndex(c => /^(name|componentname)$/i.test(c));
      if (cols.length !== n || nameIndex < 0 || cells.length % n) return fail('The Components columns or row lengths do not match. No partial inventory was produced.');
      const pathIndex = cols.findIndex(c => /location|path|directory/i.test(c));
      const stateIndex = cols.findIndex(c => /^(enabled|status|state)$/i.test(c));
      for (let i = 0; i < cells.length; i += n) {
        const row = cells.slice(i, i + n).map(c => c.trim());
        if (!row[nameIndex]) return fail('A Components row has no name. Correct the source before classifying it.');
        out.push({ name: row[nameIndex], location: pathIndex >= 0 ? row[pathIndex] || undefined : undefined, state: stateIndex >= 0 ? row[stateIndex] || undefined : undefined });
      }
    }
  } else {
    for (const [i, raw] of text.split(/\r?\n/).entries()) {
      const line = raw.trim().replace(/^(\d+[.)]|[-*•])\s+/, '');
      if (!line || line.startsWith('#')) continue;
      if (line.startsWith('@')) return fail(`Line ${i + 1}: incomplete HDA input. Paste a full Components ResultSet.`);
      const tokens = line.includes('\t') ? line.split('\t').map(v => v.trim()) : line.split(/\s+/);
      const name = tokens[0];
      if (/^(name|component|componentname)$/i.test(name)) continue;
      if (!/^[A-Za-z0-9_.-]+$/.test(name)) return fail(`Line ${i + 1}: use a component name, optionally followed by a tab-separated path and state. CSV is not accepted here.`);
      const state = tokens.slice(1).find(t => /^(enabled|disabled|true|false|on|off)$/i.test(t));
      const location = tokens.slice(1).filter(t => t !== state).join(' ') || undefined;
      out.push({ name, location, state });
    }
  }
  const seen = new Set<string>(); let duplicates = 0;
  const rows = out.filter(c => { const key = c.name.toLowerCase(); if (seen.has(key)) { duplicates++; return false; } seen.add(key); return true; });
  return { rows, duplicates };
}

export function inventoryCsv(rows: Classified[]): string {
  // Quoting alone does not prevent spreadsheet formula execution.
  const cell = (s: string) => '"' + (/^[\s]*[=+@-]/.test(s) ? "'" + s : s).replace(/"/g, '""') + '"';
  return [['name', 'category', 'location', 'state', 'note'], ...rows.map(r => [r.name, CATEGORY_LABEL[r.category], r.location ?? '', r.state ?? '', r.note ?? ''])].map(row => row.map(cell).join(',')).join('\n');
}

export const classify = (c: Component): Classified => {
  const key = c.name.toLowerCase();
  const disabled = c.state ? /^(disabled|false|off)$/i.test(c.state) : false;
  let category: Category;
  if (CUSTOM_NAME.test(c.name) || (c.location && CUSTOM_PATH.test(c.location))) category = 'custom';
  else if (ORACLE_STOCK.has(key)) category = 'oracle';
  else if (ORACLE_FAMILY.test(c.name)) category = 'family';
  else category = 'unknown';
  const notes: string[] = [];
  if (NOTES[key]) notes.push(NOTES[key]);
  if (disabled) notes.push('Disabled today. Decide whether it travels at all.');
  if (category === 'unknown') notes.push('Open the component’s .hda and manifest: compare the vendor, description and source with the installed release.');
  return { ...c, category, note: notes.join(' ') || undefined };
};
